import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function useLessonResult({ questions = [], exam = {}, elapsedSeconds = 0, lesson = {} }) {
    const { t } = useTranslation('common');
    const [filterMode, setFilterMode] = useState('all'); // 'all' | 'solved' | 'mistakes' | 'unsolved'
    const [selectedLevel, setSelectedLevel] = useState(null); // null | 'easy' | 'medium' | 'hard'
    const [selectedSkill, setSelectedSkill] = useState(null); // null | { type, id, name, questionIds }
    const [expandedSkillGroups, setExpandedSkillGroups] = useState({
        dimensions: true,
        other: true,
    });

    const toggleSkillGroup = (groupKey) => {
        setExpandedSkillGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    const norm = (str) => (str || '').toString().trim().toLowerCase();

    // 1. Process all valid non-type-11 questions and link each question to its exact DB skills
    const results = useMemo(() => {
        const qs = Array.isArray(questions) ? questions : [];
        const answersList = Array.isArray(exam?.current) ? exam.current : [];
        const marksList = Array.isArray(exam?.marks) ? exam.marks : [];
        const allLessonSkills = Array.isArray(lesson?.Skills) ? lesson.Skills : [];

        const isType11 = (q) => {
            if (!q) return true;
            const typeId = q.Type?.Id ?? q.Type ?? q.TypeId;
            return Number(typeId) === 11;
        };

        return qs
            .filter(q => !isType11(q))
            .sort((a, b) => (Number(a?.Sort ?? 0)) - (Number(b?.Sort ?? 0)))
            .map((q, index) => {
                const markObj = marksList.find(m => m && Number(m.Id) === Number(q.Id));
                const answerObj = answersList.find(a => a && Number(a.Id) === Number(q.Id));

                const maxMark = Number(q.Mark ?? 0);
                const rawUserMark = markObj ? markObj.userMark : (answerObj ? answerObj.mark : 0);
                const userMark = Math.max(0, Math.min(maxMark, Number(rawUserMark ?? 0)));

                const hasAnswer = Boolean(
                    markObj ||
                    (answerObj && answerObj.answers !== undefined && answerObj.answers !== null &&
                        (Array.isArray(answerObj.answers) ? answerObj.answers.length > 0 : true))
                );

                const isCorrect = maxMark > 0 && userMark === maxMark;
                const isMistake = hasAnswer && userMark < maxMark;
                const isUnanswered = !hasAnswer;
                const pct = maxMark > 0 ? Math.round((userMark / maxMark) * 100) : 0;

                // Determine Difficulty Level
                const rawLevel = q.Level?.Id ?? q.Level ?? 1;
                let levelKey = 'easy';
                if (typeof rawLevel === 'number') {
                    levelKey = rawLevel <= 1 ? 'easy' : rawLevel === 2 ? 'medium' : 'hard';
                } else {
                    const s = String(rawLevel).toLowerCase();
                    if (s.startsWith('m') || s.includes('متوسط')) levelKey = 'medium';
                    else if (s.startsWith('h') || s.includes('صعب')) levelKey = 'hard';
                    else levelKey = 'easy';
                }

                const levelLabel = levelKey === 'easy'
                    ? (t('Pages.Lessons.Summary.levelEasy') || 'سهل')
                    : levelKey === 'medium'
                        ? (t('Pages.Lessons.Summary.levelMedium') || 'متوسط')
                        : (t('Pages.Lessons.Summary.levelHard') || 'صعب');

                // Extract all skills linked to this specific question from DB
                // 1. From lesson.Skills where QuestionId === q.Id
                const skillsFromLesson = allLessonSkills.filter(ls =>
                    ls && ls.QuestionId && (Number(ls.QuestionId) === Number(q.Id) || String(ls.QuestionId) === String(q.Id))
                );

                // 2. From q.Skills / q.Topics / q.SkillIds
                const directSkills = Array.isArray(q.Skills)
                    ? q.Skills
                    : (Array.isArray(q.Topics) ? q.Topics : (Array.isArray(q.SkillIds) ? q.SkillIds : []));

                const mergedSkills = [...skillsFromLesson];
                directSkills.forEach(ds => {
                    if (!ds) return;
                    const dsId = typeof ds === 'object' && ds !== null ? (ds.Id || ds.SkillId || ds.TopicId) : ds;
                    const dsName = typeof ds === 'object' && ds !== null ? (ds.SkillName || ds.Name || ds.TopicName) : null;
                    const exists = mergedSkills.some(s => {
                        const sId = s.Id || s.SkillId || s.TopicId;
                        const sName = s.SkillName || s.Name || s.TopicName;
                        return (dsId && sId && Number(dsId) === Number(sId)) || (dsName && sName && norm(dsName) === norm(sName));
                    });
                    if (!exists) {
                        mergedSkills.push(ds);
                    }
                });

                return {
                    question: q,
                    index: index + 1,
                    userMark,
                    maxMark,
                    hasAnswer,
                    isCorrect,
                    isMistake,
                    isUnanswered,
                    percentage: pct,
                    levelKey,
                    levelLabel,
                    skills: mergedSkills,
                    userAnswers: answerObj?.answers ?? null
                };
            });
    }, [questions, exam, lesson, t]);

    // 2. Global Aggregations
    const totalMax = useMemo(() => results.reduce((sum, r) => sum + r.maxMark, 0), [results]);
    const totalUser = useMemo(() => results.reduce((sum, r) => sum + r.userMark, 0), [results]);
    const percentage = useMemo(() => totalMax > 0 ? Math.round((totalUser / totalMax) * 100) : 0, [totalMax, totalUser]);

    const totalQuestions = results.length;
    const solvedCount = useMemo(() => results.filter(r => r.hasAnswer).length, [results]);
    const correctCount = useMemo(() => results.filter(r => r.isCorrect).length, [results]);
    const mistakesCount = useMemo(() => results.filter(r => r.isMistake).length, [results]);
    const unsolvedCount = useMemo(() => results.filter(r => r.isUnanswered).length, [results]);

    // Grade Band (excellent, veryGood, good, average, partial, failed)
    const resultState = useMemo(() => {
        const p = percentage;
        if (p >= 90) return 'excellent';
        if (p >= 80) return 'veryGood';
        if (p >= 70) return 'good';
        if (p >= 60) return 'average';
        if (p >= 50) return 'partial';
        return 'failed';
    }, [percentage]);

    const resultText = useMemo(() => {
        const customText = t(`Pages.Lessons.Summary.${resultState}`);
        if (customText) return customText;
        if (resultState === 'excellent') return 'ممتاز';
        if (resultState === 'veryGood') return 'جيد جداً';
        if (resultState === 'good') return 'جيد';
        if (resultState === 'average') return 'متوسط';
        if (resultState === 'partial') return 'مقبول';
        return 'بحاجة لتحسين';
    }, [resultState, t]);

    const chipTone = useMemo(() => {
        if (resultState === 'failed') return 'fail';
        if (resultState === 'average' || resultState === 'partial') return 'partial';
        return 'success';
    }, [resultState]);

    // 3. Difficulty Level Statistics
    const difficultyStats = useMemo(() => {
        const init = {
            easy: { total: 0, solved: 0, correct: 0, mistakes: 0, unsolved: 0, userMarks: 0, maxMarks: 0 },
            medium: { total: 0, solved: 0, correct: 0, mistakes: 0, unsolved: 0, userMarks: 0, maxMarks: 0 },
            hard: { total: 0, solved: 0, correct: 0, mistakes: 0, unsolved: 0, userMarks: 0, maxMarks: 0 },
        };

        results.forEach(r => {
            const k = r.levelKey;
            if (init[k]) {
                init[k].total += 1;
                init[k].maxMarks += r.maxMark;
                init[k].userMarks += r.userMark;
                if (r.hasAnswer) init[k].solved += 1;
                if (r.isCorrect) init[k].correct += 1;
                if (r.isMistake) init[k].mistakes += 1;
                if (r.isUnanswered) init[k].unsolved += 1;
            }
        });

        const calc = (data, key, label, icon) => {
            const accuracy = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
            const markPct = data.maxMarks > 0 ? Math.round((data.userMarks / data.maxMarks) * 100) : 0;
            return {
                ...data,
                key,
                label,
                icon,
                accuracy,
                markPct
            };
        };

        return {
            easy: calc(init.easy, 'easy', t('Pages.Lessons.Summary.levelEasy') || 'سهل', 'feather-alt'),
            medium: calc(init.medium, 'medium', t('Pages.Lessons.Summary.levelMedium') || 'متوسط', 'tachometer-alt'),
            hard: calc(init.hard, 'hard', t('Pages.Lessons.Summary.levelHard') || 'صعب', 'fire-alt'),
        };
    }, [results, t]);

    // 4. Skills Evaluation: Map each skill to its exact linked questions from DB
    const skillsAnalysis = useMemo(() => {
        const displaySkills = Array.isArray(lesson?.Skills) ? lesson.Skills : [];
        const rawQuestions = Array.isArray(questions) ? questions : [];

        // Exclude type 11 questions
        const type11QuestionIds = new Set(
            rawQuestions
                .filter(q => {
                    const typeId = q?.Type?.Id ?? q?.Type ?? q?.TypeId;
                    return Number(typeId) === 11;
                })
                .map(q => q.Id)
        );

        // Helper to check if a result item has this skill linked from DB
        const isQuestionLinkedToSkill = (r, skillDef) => {
            if (!r || !skillDef) return false;
            const q = r.question;
            if (!q || type11QuestionIds.has(q.Id)) return false;

            const sName = norm(skillDef.SkillName || skillDef.Name || skillDef.name);
            const sId = skillDef.Id || skillDef.SkillId || skillDef.id || skillDef.TopicId;

            // 1. Direct QuestionId on skill row
            if (skillDef.QuestionId && (Number(skillDef.QuestionId) === Number(q.Id) || String(skillDef.QuestionId) === String(q.Id))) {
                return true;
            }

            // 2. Direct question ID match on question object
            if (q.SkillId && sId && Number(q.SkillId) === Number(sId)) {
                return true;
            }

            // 3. Question's skills array
            const qSkillList = [
                ...(Array.isArray(r.skills) ? r.skills : []),
                ...(Array.isArray(q.Skills) ? q.Skills : []),
                ...(Array.isArray(q.Topics) ? q.Topics : [])
            ];

            const inQSkills = qSkillList.some(qs => {
                if (!qs) return false;
                const qsName = norm(typeof qs === 'string' ? qs : (qs.SkillName || qs.Name || qs.TopicName));
                const qsId = typeof qs === 'object' && qs !== null ? (qs.Id || qs.SkillId || qs.TopicId) : qs;
                if (sId && qsId && Number(sId) === Number(qsId)) return true;
                if (sName && qsName && sName === qsName) return true;
                return false;
            });
            if (inQSkills) return true;

            // 4. Match via lesson.Skills
            const inLessonSkills = displaySkills.some(ls => {
                if (!ls) return false;
                const lsName = norm(ls.SkillName || ls.Name);
                const lsId = ls.Id || ls.SkillId;
                const isSameSkill = (sId && lsId && Number(sId) === Number(lsId)) || (sName && lsName && sName === lsName);
                return isSameSkill && ls.QuestionId && (Number(ls.QuestionId) === Number(q.Id) || String(ls.QuestionId) === String(q.Id));
            });
            if (inLessonSkills) return true;

            return false;
        };

        const skillMap = {};

        // Parse skills from lesson.Skills
        displaySkills.forEach(s => {
            if (s.QuestionId && type11QuestionIds.has(s.QuestionId)) return;
            if (Number(s.TypeId) === 11) return;

            const skillName = s.SkillName || s.Name || s.TopicName;
            if (!skillName) return;
            const key = String(skillName).trim();
            if (!key) return;

            if (!skillMap[key]) {
                skillMap[key] = {
                    id: s.Id || s.SkillId,
                    name: key,
                    dimensionName: s.DimensionName || s.DomainName,
                    dimensionComponentName: s.DimensionComponentName,
                    domainName: s.DomainName,
                    subDomainName: s.SubDomainName,
                    associatedQuestions: []
                };
            }

            // Find all matching questions in results linked to this skill
            results.forEach(r => {
                if (isQuestionLinkedToSkill(r, s) && !skillMap[key].associatedQuestions.includes(r.question.Id)) {
                    skillMap[key].associatedQuestions.push(r.question.Id);
                }
            });
        });

        // Also parse skills on questions in results
        results.forEach(r => {
            const questionSkills = [
                ...(Array.isArray(r.skills) ? r.skills : []),
                ...(Array.isArray(r.question?.Skills) ? r.question.Skills : []),
                ...(Array.isArray(r.question?.Topics) ? r.question.Topics : [])
            ];

            questionSkills.forEach(qs => {
                if (!qs) return;
                const qsName = typeof qs === 'string' ? qs : (qs.SkillName || qs.Name || qs.TopicName);
                if (!qsName) return;
                const key = String(qsName).trim();
                if (!key) return;

                if (!skillMap[key]) {
                    skillMap[key] = {
                        id: typeof qs === 'object' && qs !== null ? (qs.Id || qs.SkillId || qs.TopicId) : null,
                        name: key,
                        dimensionName: qs.DimensionName || qs.DomainName,
                        dimensionComponentName: qs.DimensionComponentName,
                        domainName: qs.DomainName,
                        subDomainName: qs.SubDomainName,
                        associatedQuestions: []
                    };
                }

                if (!skillMap[key].associatedQuestions.includes(r.question.Id)) {
                    skillMap[key].associatedQuestions.push(r.question.Id);
                }
            });
        });

        // Calculate Level 3: Individual Skill stats based ONLY on questions linked to it
        const evaluatedSkills = Object.values(skillMap).map(item => {
            const matchedResults = results.filter(r => item.associatedQuestions.includes(r.question.Id));
            const totalQuestions = matchedResults.length;
            const correctQuestions = matchedResults.filter(r => r.isCorrect).length;
            const maxMarks = matchedResults.reduce((sum, r) => sum + r.maxMark, 0);
            const userMarks = matchedResults.reduce((sum, r) => sum + r.userMark, 0);

            const masteryPct = maxMarks > 0
                ? Math.round((userMarks / maxMarks) * 100)
                : (totalQuestions > 0 ? Math.round((correctQuestions / totalQuestions) * 100) : 0);

            // Strong Points: student answered questions correctly with high score (≥ 75% and userMarks > 0)
            const isStrong = totalQuestions > 0 && correctQuestions > 0 && userMarks > 0 && masteryPct >= 75;
            // Weak Points: student had mistakes, uncompleted answers, or low score (< 60%)
            const isWeak = totalQuestions > 0 && masteryPct < 60;
            const isModerate = totalQuestions > 0 && !isStrong && !isWeak;

            return {
                ...item,
                totalQuestions,
                correctQuestions,
                maxMarks,
                userMarks,
                masteryPct,
                isStrong,
                isWeak,
                isModerate,
                status: isStrong ? 'strong' : isWeak ? 'weak' : 'moderate'
            };
        });

        // Strong Points (≥ 75%) & Weak Points (< 60%)
        const strongSkills = evaluatedSkills
            .filter(s => s.isStrong)
            .sort((a, b) => b.masteryPct - a.masteryPct);

        const weakSkills = evaluatedSkills
            .filter(s => s.isWeak)
            .sort((a, b) => a.masteryPct - b.masteryPct);

        const moderateSkills = evaluatedSkills
            .filter(s => s.isModerate)
            .sort((a, b) => b.masteryPct - a.masteryPct);

        // Helper to aggregate stats for Level 1 and Level 2 groups
        const aggregateGroupStats = (skillsList) => {
            const qIds = Array.from(new Set(skillsList.flatMap(s => s.associatedQuestions)));
            const groupResults = results.filter(r => qIds.includes(r.question.Id));
            const totalQuestions = groupResults.length;
            const correctQuestions = groupResults.filter(r => r.isCorrect).length;
            const maxMarks = groupResults.reduce((sum, r) => sum + r.maxMark, 0);
            const userMarks = groupResults.reduce((sum, r) => sum + r.userMark, 0);
            const masteryPct = maxMarks > 0
                ? Math.round((userMarks / maxMarks) * 100)
                : (totalQuestions > 0 ? Math.round((correctQuestions / totalQuestions) * 100) : 0);

            return {
                totalQuestions,
                correctQuestions,
                maxMarks,
                userMarks,
                masteryPct,
                questionIds: qIds
            };
        };

        // Construct 3-Level Trees with Points & Results at EACH level (No Elements):
        // A. Curriculum Dimensions & Components Tree
        const domainMap = {};
        evaluatedSkills.forEach(s => {
            if (s.dimensionComponentName) {
                const domainName = s.dimensionName || s.domainName || (t('QuestionView.uncategorized') || 'المجال العام');
                const compName = s.dimensionComponentName;
                if (!domainMap[domainName]) domainMap[domainName] = {};
                if (!domainMap[domainName][compName]) domainMap[domainName][compName] = [];
                domainMap[domainName][compName].push(s);
            }
        });

        const domainTree = Object.keys(domainMap).map(domainName => {
            const compKeys = Object.keys(domainMap[domainName]);
            const allDomainSkills = compKeys.flatMap(k => domainMap[domainName][k]);
            const domainStats = aggregateGroupStats(allDomainSkills);

            return {
                name: domainName,
                ...domainStats,
                components: compKeys.map(compName => {
                    const compSkills = domainMap[domainName][compName];
                    const compStats = aggregateGroupStats(compSkills);
                    return {
                        name: compName,
                        ...compStats,
                        skills: compSkills
                    };
                })
            };
        });

        // B. Other Skills Tree
        const otherSkillsDomainMap = {};
        evaluatedSkills.forEach(s => {
            if (!s.dimensionComponentName) {
                const domainName = s.domainName || (t('OtherSkills') || 'مهارات أخرى');
                const subDomainName = s.subDomainName || 'مهارات عامة';
                if (!otherSkillsDomainMap[domainName]) otherSkillsDomainMap[domainName] = {};
                if (!otherSkillsDomainMap[domainName][subDomainName]) otherSkillsDomainMap[domainName][subDomainName] = [];
                otherSkillsDomainMap[domainName][subDomainName].push(s);
            }
        });

        const otherSkillsTree = Object.keys(otherSkillsDomainMap).map(domainName => {
            const subKeys = Object.keys(otherSkillsDomainMap[domainName]);
            const allSkills = subKeys.flatMap(k => otherSkillsDomainMap[domainName][k]);
            const domainStats = aggregateGroupStats(allSkills);

            return {
                name: domainName,
                ...domainStats,
                subDomains: subKeys.map(subName => {
                    const subSkills = otherSkillsDomainMap[domainName][subName];
                    const subStats = aggregateGroupStats(subSkills);
                    return {
                        name: subName,
                        ...subStats,
                        skills: subSkills
                    };
                })
            };
        });

        const totalSkillsCount = evaluatedSkills.length;
        const hasSkills = totalSkillsCount > 0;

        return {
            allSkills: evaluatedSkills,
            strongSkills,
            weakSkills,
            moderateSkills,
            domainTree,
            otherSkillsTree,
            totalSkillsCount,
            hasSkills
        };
    }, [lesson, questions, results, t]);

    // 5. Filtered questions for review (supporting FilterMode + SelectedLevel + SelectedSkill)
    const filteredResults = useMemo(() => {
        return results.filter(r => {
            // Filter by Mode
            if (filterMode === 'solved' && !r.isCorrect) return false;
            if (filterMode === 'mistakes' && !r.isMistake) return false;
            if (filterMode === 'unsolved' && !r.isUnanswered) return false;

            // Filter by Difficulty Level
            if (selectedLevel && r.levelKey !== selectedLevel) return false;

            // Filter by Selected Skill / Component / Domain
            if (selectedSkill) {
                const targetName = norm(selectedSkill.name);
                const targetId = selectedSkill.id;
                const filterType = selectedSkill.type; // 'domain' | 'component' | 'skill'
                const qIds = Array.isArray(selectedSkill.questionIds) ? selectedSkill.questionIds.map(String) : [];

                // Match 1: By Question ID list in associatedQuestions
                if (qIds.length > 0 && qIds.includes(String(r.question.Id))) {
                    return true;
                }

                // Match 2: By skills attached on this result / question
                const qSkills = [
                    ...(Array.isArray(r.skills) ? r.skills : []),
                    ...(Array.isArray(r.question?.Skills) ? r.question.Skills : []),
                    ...(Array.isArray(r.question?.Topics) ? r.question.Topics : [])
                ];

                const matchInSkills = qSkills.some(s => {
                    if (!s) return false;
                    const sName = norm(typeof s === 'string' ? s : (s.SkillName || s.Name || s.TopicName));
                    const sId = typeof s === 'object' && s !== null ? (s.Id || s.SkillId || s.TopicId) : s;
                    const dimName = norm(s.DimensionName || s.DomainName);
                    const compName = norm(s.DimensionComponentName || s.SubDomainName);

                    if (filterType === 'domain' && targetName && dimName === targetName) return true;
                    if (filterType === 'component' && targetName && compName === targetName) return true;
                    if (targetId && sId && Number(targetId) === Number(sId)) return true;
                    if (targetName && sName && targetName === sName) return true;
                    return false;
                });
                if (matchInSkills) return true;

                // Match 3: By lesson.Skills
                const matchInLesson = (lesson?.Skills || []).some(ls => {
                    if (!ls) return false;
                    const lsName = norm(ls.SkillName || ls.Name);
                    const lsId = ls.Id || ls.SkillId;
                    const dimName = norm(ls.DimensionName || ls.DomainName);
                    const compName = norm(ls.DimensionComponentName || ls.SubDomainName);
                    const isQ = ls.QuestionId && (String(ls.QuestionId) === String(r.question.Id));

                    if (!isQ) return false;
                    if (filterType === 'domain' && targetName && dimName === targetName) return true;
                    if (filterType === 'component' && targetName && compName === targetName) return true;
                    if (targetId && lsId && Number(targetId) === Number(lsId)) return true;
                    if (targetName && lsName && targetName === lsName) return true;
                    return false;
                });
                if (matchInLesson) return true;

                return false;
            }

            return true;
        });
    }, [results, filterMode, selectedLevel, selectedSkill, lesson]);

    // 6. Formatted Elapsed Time
    const formattedTime = useMemo(() => {
        const total = Math.max(0, Math.floor(elapsedSeconds));
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        const pad = n => n.toString().padStart(2, '0');
        return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
    }, [elapsedSeconds]);

    const avgTimePerQuestion = useMemo(() => {
        if (results.length === 0) return '00:00';
        const avg = Math.floor(elapsedSeconds / results.length);
        const m = Math.floor(avg / 60);
        const s = avg % 60;
        const pad = n => n.toString().padStart(2, '0');
        return `${pad(m)}:${pad(s)}`;
    }, [elapsedSeconds, results.length]);

    const resetFilters = () => {
        setFilterMode('all');
        setSelectedLevel(null);
        setSelectedSkill(null);
    };

    return {
        results,
        filteredResults,
        filterMode,
        setFilterMode,
        selectedLevel,
        setSelectedLevel,
        selectedSkill,
        setSelectedSkill,
        resetFilters,
        totalMax,
        totalUser,
        percentage,
        totalQuestions,
        solvedCount,
        correctCount,
        mistakesCount,
        unsolvedCount,
        resultState,
        resultText,
        chipTone,
        difficultyStats,
        skillsAnalysis,
        formattedTime,
        avgTimePerQuestion,
        expandedSkillGroups,
        toggleSkillGroup
    };
}
